/*
 * Copyright 2021 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { JSONSchema7, JSONSchema7Definition } from 'json-schema';
import useAsync from 'react-use/lib/useAsync';
import { scaffolderApiRef } from '../../api';

import {
  WbBadge,
  WbBreadcrumb,
  WbBreadcrumbs,
  WbHeader,
} from '@agilelab/plugin-wb-platform';
import { Content, ErrorPage, Progress } from '@mainstage/core-components';
import { useApi, useRouteRef } from '@mainstage/core-plugin-api';
import { templateIndexRouteRef } from '../../routes';
import { WbPage } from '@agilelab/plugin-wb-platform';
import clsx from 'clsx';

const useStyles = makeStyles(theme => ({
  code: {
    fontFamily: 'Menlo, monospace',
    padding: theme.spacing(1),
    backgroundColor: theme.palette.bkg.secondary,
    display: 'inline-block',
    borderRadius: 5,
    border: `1px solid ${theme.palette.secondary.light}`,
    position: 'relative',
    color: theme.palette.getContrastText(theme.palette.bkg.secondary),
    maxWidth: '100%',
    wordBreak: 'break-all',
  },

  codeRequired: {
    '&::after': {
      content: '"*"',
    },
  },
}));

export const ActionsPage = () => {
  const api = useApi(scaffolderApiRef);
  const adminTemplateIndexRoute = useRouteRef(templateIndexRouteRef);

  const classes = useStyles();
  const { loading, value, error } = useAsync(async () => {
    return api.listActions();
  });

  if (loading) {
    return <Progress />;
  }

  if (error) {
    return (
      <ErrorPage
        statusMessage="Failed to load installed actions"
        status="500"
      />
    );
  }

  const formatRows = (input: JSONSchema7) => {
    const properties = input.properties;
    if (!properties) {
      return undefined;
    }

    return Object.entries(properties).map(entry => {
      const [key] = entry;
      const props = entry[1] as unknown as JSONSchema7;
      const required = input.required?.includes(key);

      return (
        <TableRow key={key}>
          <TableCell>
            <WbBadge
              color="secondary"
              className={clsx(required && classes.codeRequired)}
            >
              {key}
            </WbBadge>
          </TableCell>
          <TableCell>{props.title}</TableCell>
          <TableCell>{props.description}</TableCell>
          <TableCell>
            <WbBadge color="secondary">{props.type}</WbBadge>
          </TableCell>
        </TableRow>
      );
    });
  };

  const renderTable = (input: JSONSchema7) => {
    if (!input.properties) {
      return undefined;
    }
    return (
      // focusable to allow scroll via keyboard
      <TableContainer component={Paper} tabIndex={0}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Type</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>{formatRows(input)}</TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderTables = (name: string, input?: JSONSchema7Definition[]) => {
    if (!input) {
      return undefined;
    }

    return (
      <>
        <Typography variant="h6" paragraph>
          {name}
        </Typography>
        {input.map((i, index) => (
          <div key={index}>{renderTable(i as unknown as JSONSchema7)}</div>
        ))}
      </>
    );
  };

  const items = value?.map(action => {
    if (action.id.startsWith('legacy:')) {
      return undefined;
    }

    const oneOf = renderTables('oneOf', action.schema?.input?.oneOf);
    return (
      <Box pb={4} key={action.id}>
        <Typography variant="h4" paragraph className={classes.code}>
          {action.id}
        </Typography>
        <Typography>{action.description}</Typography>
        {action.schema?.input && (
          <Box pb={2}>
            <Typography variant="h5" paragraph>
              Input
            </Typography>
            {renderTable(action.schema.input)}
            {oneOf}
          </Box>
        )}
        {action.schema?.output && (
          <Box pb={2}>
            <Typography variant="h5" paragraph>
              Output
            </Typography>
            {renderTable(action.schema.output)}
          </Box>
        )}
      </Box>
    );
  });

  return (
    <WbPage themeId="home">
      <WbHeader
        pageTitleOverride="Create a New Component"
        title="Installed actions"
        breadcrumbs={
          <WbBreadcrumbs aria-label="breadcrumb">
            <WbBreadcrumb>Administration</WbBreadcrumb>
            <WbBreadcrumb to={adminTemplateIndexRoute()}>
              Templates
            </WbBreadcrumb>
            <WbBreadcrumb>Installed actions</WbBreadcrumb>
          </WbBreadcrumbs>
        }
      />
      <Content>{items}</Content>
    </WbPage>
  );
};
